import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Tire } from "@phosphor-icons/react";
import { AdminHeader, Field, inputCls, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BLANK = { name: "", inner_bearing: "", outer_bearing: "", seal: "", next_size_id: "" };
const PART_FIELDS = [["inner_bearing", "Inner Bearing No.", "LM67048 / LM67010"], ["outer_bearing", "Outer Bearing No.", "LM12749 / LM12710"], ["seal", "Seal No.", "e.g. 400-7"]];

function HubRow({ r, nextName, onEdit, onDelete }) {
  return (
    <tr data-testid="hub-type-row" className="hover:bg-white/[0.02]">
      <td className="px-5 py-4 font-heading font-semibold text-white">{r.name}</td>
      {PART_FIELDS.map(([k]) => <td key={k} className="px-5 py-4 font-mono text-sm text-neutral-300">{r[k] || "—"}</td>)}
      <td className="px-5 py-4 text-sm text-neutral-400" data-testid="hub-type-next">{nextName || "—"}</td>
      <td className="px-5 py-4 text-sm text-neutral-400" data-testid="hub-type-vans">
        {r.vans.admin} set by OTM
        {r.vans.customer > 0 && <span className="block text-xs text-amber-300/90">{r.vans.customer} chosen by customers</span>}
      </td>
      <td className="px-5 py-4">
        <div className="flex justify-end gap-1">
          <button onClick={() => onEdit(r)} data-testid="hub-type-edit" className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/[0.05]"><PencilSimple size={16} /></button>
          <button onClick={() => onDelete(r)} data-testid="hub-type-delete" className="p-2 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10"><Trash size={16} /></button>
        </div>
      </td>
    </tr>
  );
}

function HubDialog({ editing, setEditing, onSave, saving, hubs }) {
  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
      <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-white">{editing?.id ? "Edit" : "Add"} Hub Type</DialogTitle></DialogHeader>
        {editing && (
          <div className="space-y-4">
            <Field label="Hub Name / Brand"><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} data-testid="hub-type-name" placeholder='e.g. AL-KO 12" Electric Drum' /></Field>
            {PART_FIELDS.map(([k, label, ph]) => (
              <Field key={k} label={label}><input value={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className={`${inputCls} font-mono`} data-testid={`hub-type-${k}`} placeholder={ph} /></Field>
            ))}
            <Field label="Next size up (e.g. for a 4T ATM upgrade)">
              <select value={editing.next_size_id || ""} onChange={(e) => setEditing({ ...editing, next_size_id: e.target.value })} className={inputCls} data-testid="hub-type-next-size">
                <option value="">— None —</option>
                {hubs.filter((h) => h.id !== editing.id).map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <GhostButton onClick={() => setEditing(null)}>Cancel</GhostButton>
              <PrimaryButton onClick={onSave} disabled={saving} data-testid="hub-type-save">{saving ? "Saving…" : "Save"}</PrimaryButton>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminHubTypes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/hub-types")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(getErrorMessage(e, "Couldn't load hub types. Check your connection and retry.")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Hub name is required"); return; }
    setSaving(true);
    try {
      if (editing.id) await adminApi.put(`/admin/hub-types/${editing.id}`, editing);
      else await adminApi.post("/admin/hub-types", editing);
      toast.success("Hub type saved");
      setEditing(null); load();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const del = async (r) => {
    const used = r.vans.admin + r.vans.customer;
    if (!window.confirm(`Delete "${r.name}"?${used ? ` It will be removed from ${used} caravan(s).` : ""}`)) return;
    try { await adminApi.delete(`/admin/hub-types/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div>
      <AdminHeader
        title="Hub Types"
        subtitle="Hubs and their bearing and seal numbers. Set the 'next size up' so one rule can upsize hubs (e.g. 4T ATM upgrade). Customers can pick their hubs on My Caravan if none is set; Nick quotes these numbers in chat."
        actions={<PrimaryButton onClick={() => setEditing({ ...BLANK })} data-testid="hub-type-new"><Plus size={16} /> Add Hub Type</PrimaryButton>}
      />
      <div className="p-8">
        {loading ? <div className="text-neutral-600">Loading...</div> : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3" data-testid="hub-types-empty">
            <Tire size={36} className="text-neutral-700" weight="duotone" /> No hub types yet. Add your first one.
          </div>
        ) : (
          <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[960px]" data-testid="hub-types-table">
              <thead>
                <tr className="text-left text-[0.65rem] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                  {["Hub", "Inner Bearing", "Outer Bearing", "Seal", "Next size up", "Vans"].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {rows.map((r) => <HubRow key={r.id} r={r} nextName={rows.find((h) => h.id === r.next_size_id)?.name} onEdit={(x) => setEditing({ ...BLANK, ...x })} onDelete={del} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <HubDialog editing={editing} setEditing={setEditing} onSave={save} saving={saving} hubs={rows} />
    </div>
  );
}
